import { useParams } from 'react-router-dom';
import Api from '../Api';
import { usePools } from '../DatabaseContext';
import React, { useEffect, useState } from 'react';
import FormContainer from '../Fields/FormContainer';
import SaveAndContinue from '../Fields/SaveAndContinue';
import SelectQuestion from '../Fields/SelectQuestion';
import ChoiceQuestion from '../Fields/ChoiceQuestion';
import BubbleLoader from '../../../Libraries/Loader/bubble-loader';
import sortBy from 'lodash/sortBy'
import times from 'lodash/times'

export default function SeasonPage({ i18n }) {
    const { id } = useParams()
    const { setPoolId, pool } = usePools()
    const [start, setStart] = useState('')
    const [end, setEnd] = useState('')
    const [cityData, setCityData] = useState([])
    const [coldestMonth, setColdestMonth] = useState(null)
    const [airTemperatureChoice, setAirTemperatureChoice] = useState('')

    const MONTHS = i18n.months.map(row => {
        return {
            value: row[0],
            label: row[1],
        }
    })

    const updateFields = async () => {
        setStart(pool?.season_start)
        setEnd(pool?.season_end)
        setAirTemperatureChoice(pool?.air_temperature_choice)

        const { data: { cities } } = await Api.getCityData()

        setCityData(cities)
    }

    const notReady = () => {
        return !(
            id &&
            pool &&
            start !== undefined &&
            end !== undefined &&
            cityData.length &&
            airTemperatureChoice !== undefined
        );
    }

    const findColdestMonth = () => {
        const selectedCity = cityData.find(city => city.name === pool?.city && pool?.country === city.country)

        if (!cityData.length || !selectedCity) {
            return;
        }

        let { data: { months }  } = selectedCity

        months = sortBy(months, 'order')

        let range = []

        const startIndex = months.find(month => month.label === start).order
        const endIndex = months.find(month => month.label === end).order

        if (startIndex !== endIndex) {
            const steps = Math.abs((startIndex > endIndex ? 12 : 0) - (Math.max(startIndex, endIndex) - Math.min(startIndex, endIndex)))

            let counter = startIndex - 1;

            times(steps + 1, () => {
                const month = months[counter]

                if (month) {
                    range.push(month)
                } else {
                    range.push(months[counter - 12])
                }

                counter++
            })
        } else {
            range = [...months]
        }

        const coldest = sortBy(range, ['air_temperature_low', 'air_temperature', 'humidity', 'sunshine_hours'])[0]

        setColdestMonth(coldest);
    }

    // Empty clean up for memory leak on i18n
    useEffect(() => {
        return () => {
            // This is the cleanup function
        }
    }, [i18n]);

    useEffect(() => {
        setPoolId(id)
    }, [id])

    useEffect(() => {
        updateFields();
    }, [pool])

    useEffect(() => {
        if (!pool) {
            return;
        }

        findColdestMonth();
    }, [start, end, cityData])

    if (notReady()) {
        return <BubbleLoader />
    }

    return <FormContainer breadcrumb={[
        { label: pool.name, url: `/${pool.id}/name` },
        { label: i18n.dimensions.heading, url: `/${pool.id}/dimensions` },
        { label: i18n.temperature.heading, url: `/${pool.id}/temperature` },
        { label: i18n.location.heading, url: `/${pool.id}/location` },
        { label: i18n.seasons.heading },
    ]} >

        <p>{ i18n.seasons.label }</p>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
            <div className="flex-1">
                <SelectQuestion
                    value={ start }
                    onChange={ setStart }
                    name="start"
                    label={ i18n.seasons.start }
                    options={ MONTHS }
                />
            </div>
            <div className="flex-1">
                <SelectQuestion
                    value={ end }
                    onChange={ setEnd }
                    name="end"
                    label={ i18n.seasons.end }
                    options={ MONTHS }
                />
            </div>
        </div>

        <div className="mt-8">
            <ChoiceQuestion
                label={ i18n.seasons.air_temp_av }
                name="air_temperature_choice"
                onChange={ setAirTemperatureChoice }
                value={ airTemperatureChoice }
                options={[
                    { label: i18n.seasons.av_months_low, value: 'Low' },
                    { label: i18n.seasons.av_months, value: 'Average' },
                ]}
            />
        </div>

        <SaveAndContinue id={ pool?.id } data={{
            season_start: start,
            season_end: end,
            air_temperature: airTemperatureChoice === 'Low' ? coldestMonth?.air_temperature_low : coldestMonth?.air_temperature,
            air_temperature_choice: airTemperatureChoice,
            humidity: coldestMonth?.humidity,
            sunshine_hours: coldestMonth?.sunshine_hours,
        }} next="power" />

    </FormContainer>
}
